import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FolderOpen, Monitor, Trash2, Database } from 'lucide-react';
import { useFileManagerStore } from '../../store/useFileManagerStore';
import { useFileSearchStatusStore } from '../../store/useFileSearchStatus';
import { FileSearchStatus } from '../../../../types';
import { formatSize } from '../../utils/fileUtils';
import { t } from '../../i18n';
import { squarify } from './treemapLayout';
import { tileBackground } from './treemapColors';
import { useTreemapData, TreemapNode } from './useTreemapData';
import { eventBus } from '../../../../core/EventBus';

const TreemapView: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [menu, setMenu] = useState<{ x: number; y: number; node: TreemapNode } | null>(null);

  const { currentPath, files, setCurrentPath, selectedPaths, setSelectedPaths, setViewMode, deleteFiles, fs, openSearchEngineInitModal } =
    useFileManagerStore();
  const status = useFileSearchStatusStore((s) => s.status);
  const isReady = status === FileSearchStatus.READY;
  const isRoot = currentPath === 'root';

  const { nodes, total, loading, progress } = useTreemapData(currentPath, files);

  // Track container size for the layout.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0].contentRect;
      setDims({ w: r.width, h: r.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tiles = useMemo(() => {
    if (dims.w <= 0 || dims.h <= 0 || nodes.length === 0) return [];
    return squarify(nodes, (n) => n.size, { x: 0, y: 0, w: dims.w, h: dims.h });
  }, [nodes, dims]);

  const handleClick = (node: TreemapNode) => {
    if (node.isOther) return;
    if (node.isDir) {
      setCurrentPath(node.path);
    } else {
      setSelectedPaths([node.path]);
    }
  };

  const handleContextMenu = (e: React.MouseEvent, node: TreemapNode) => {
    e.preventDefault();
    e.stopPropagation();
    if (node.isOther) return; // aggregated tile has no real file
    setMenu({ x: e.clientX, y: e.clientY, node });
  };

  // Close the context menu on outside click / Esc / window resize.
  useEffect(() => {
    if (!menu) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu(null);
    };
    const onResize = () => setMenu(null);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [menu]);

  const tooltip = (node: TreemapNode): string => {
    const pct = total > 0 ? ((node.size / total) * 100).toFixed(1) : '0';
    const lines = [node.name, formatSize(node.size), `${pct}%`];
    if (node.fileCount != null) lines.push(`${node.fileCount} 个文件`);
    return lines.join('\n');
  };

  // Non-root view needs the search engine (folder_stats) ready.
  const needsEngine = !isRoot;
  const blocked = needsEngine && !isReady;

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden">
      {/* Header: title + load progress (echoes the reference mock) */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-black/20 shrink-0">
        <div className="text-sm text-slate-300 font-medium">{t('spaceAnalysisTitle')}</div>
        <div className="text-xs text-slate-400 tabular-nums">
          {loading ? `${progress}%` : total > 0 ? formatSize(total) : ''}
        </div>
      </div>
      {loading && (
        <div className="h-0.5 bg-white/5 shrink-0">
          <div
            className="h-full bg-win-accent transition-all duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      <div ref={containerRef} className="relative flex-1 overflow-hidden bg-slate-900/40">
        {blocked ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <div className="text-sm text-slate-400">{t('spaceAnalysisNeedEngine')}</div>
            <button
              onClick={() => openSearchEngineInitModal(false)}
              title={t('searchEngineInitTitle')}
              className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 hover:border-yellow-500/50 transition-all group"
            >
              <Database size={16} className="text-yellow-500" />
              <span className="text-sm text-slate-300 group-hover:text-white">{t('searchEngineInitTitle')}</span>
            </button>
          </div>
        ) : loading && tiles.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">
            {t('spaceAnalysisLoading')}{progress > 0 ? ` ${progress}%` : ''}
          </div>
        ) : !loading && tiles.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-500">
            {t('spaceAnalysisEmpty')}
          </div>
        ) : (
          tiles.map((tile) => {
            const node = tile.item;
            const selected = selectedPaths.includes(node.path);
            const showText = tile.w > 56 && tile.h > 34;
            const showSize = tile.w > 56 && tile.h > 54;
            return (
              <div
                key={node.path}
                onClick={() => handleClick(node)}
                onContextMenu={(e) => handleContextMenu(e, node)}
                title={tooltip(node)}
                className={`absolute box-border overflow-hidden flex flex-col items-center justify-center text-center transition-[filter] ${
                  node.isOther ? 'cursor-default' : 'cursor-pointer hover:brightness-110'
                }`}
                style={{
                  left: tile.x,
                  top: tile.y,
                  width: tile.w,
                  height: tile.h,
                  background: tileBackground(node.name, node.isDir),
                  border: selected ? '2px solid #fff' : '1px solid rgba(0,0,0,0.35)',
                }}
              >
                {showText && (
                  <span className="px-1 text-[13px] leading-tight text-white font-bold drop-shadow truncate max-w-full">
                    {node.name}
                  </span>
                )}
                {showSize && (
                  <span className="px-1 text-[12px] leading-tight text-white/90 font-semibold drop-shadow">
                    {formatSize(node.size)}
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      {menu &&
        createPortal(
          <div
            ref={menuRef}
            className="fixed z-[200] w-56 bg-[#202020]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl py-1 text-slate-200 animate-in fade-in zoom-in-95 duration-100"
            style={{
              top: menu.y + 160 > window.innerHeight ? menu.y - 160 : menu.y,
              left: menu.x + 224 > window.innerWidth ? menu.x - 224 : menu.x,
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            <button
              onClick={() => {
                setViewMode('grid');
                setSelectedPaths([menu.node.path]);
                setMenu(null);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 mx-1 rounded text-sm hover:bg-white/10"
              style={{ width: 'calc(100% - 0.5rem)' }}
            >
              <FolderOpen size={16} className="opacity-70" />
              <span className="flex-1 text-left truncate">{t('treemapLocateInManager')}</span>
            </button>
            <button
              onClick={async () => {
                const p = menu.node.path;
                setMenu(null);
                try {
                  await (fs as any).shellOpen?.(p, 'select');
                } catch {
                  /* ignore — close the manager regardless so the user can look at the remote desktop */
                }
                eventBus.emit('fm:close', undefined as never);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 mx-1 rounded text-sm hover:bg-white/10"
              style={{ width: 'calc(100% - 0.5rem)' }}
            >
              <Monitor size={16} className="opacity-70" />
              <span className="flex-1 text-left truncate">{t('treemapLocateInRemote')}</span>
            </button>
            <div className="h-px bg-white/10 my-1 mx-2" />
            <button
              onClick={() => {
                const p = menu.node.path;
                setMenu(null);
                deleteFiles([p]);
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 mx-1 rounded text-sm text-red-400 hover:bg-white/10"
              style={{ width: 'calc(100% - 0.5rem)' }}
            >
              <Trash2 size={16} className="opacity-70" />
              <span className="flex-1 text-left truncate">{t('treemapDelete')}</span>
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TreemapView;
